'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Record, List, fromJS, Set as ImmutableSet, OrderedMap, Map as ImmutableMap } from 'immutable';
import KeyMirror from 'react-utils/keyMirror';
import { Networks, AdCreationTypes, FBLeadAdCallsToAction, LinkedInLeadAdCallsToAction, FBCampaignObjective, FileTypes } from 'AdsUI/lib/Constants';
import I18n from 'I18n';
import { getMinutesSinceMidnight } from 'AdsUI/lib/Utils';
import { fromMoment } from 'UIComponents/core/SimpleDate';
import BudgetRecord, { GoogleBiddingStrategyTypes } from './BudgetRecord';
import ScheduleRecord, { fromDraft as getScheduleFromDraft, fromPublished as getScheduleFromPublished, ScheduleTypes, SCHEDULE_TYPE_TO_SEQUENCE_TYPE } from './ScheduleRecord';
import LeadFormRecord from './LeadFormRecord';
import AdCreationGeneralTargetingRecord from './AdCreationGeneralTargetingRecord';
import AdCreationSequenceTargetingRecord from './AdCreationSequenceTargetingRecord';
import KeywordCreationRecord from './KeywordCreationRecord';
import AdCreationSearchTargetingRecord from './AdCreationSearchTargetingRecord';
import { CREATIVE_RECORD_BY_TYPE, CreativeTypes } from './AdCreationCreativeRecord';
import AdCreationWebsiteUrlRecord from './AdCreationWebsiteUrlRecord';
import { loadFromDraft as getNotificationFromDraft, loadFromPublished as getNotificationFromPublished } from 'AdsUI/records/ad-management/AdSequenceNotificationsRecord';
import FBSocialPostRecord from 'AdsUI/records/ad-management/FBSocialPostRecord'; // Draft versions:
// 1: Initial implementation
// 2: Addition of adType field
// 3: Moving leadSyncingEntityId and campaignId off of CreativeRecord, pluralize creative, move websiteUrl to creative
// 4: LinkedIn lookalike facet handling

const CURRENT_DRAFT_VERSION = 4;
export const AD_MODE = KeyMirror({
  CREATE: null,
  EDIT: null
});
export const TYPE_TO_CAMPAIGN_OBJECTIVE = {
  [AdCreationTypes.LEAD_AD]: 'LEAD_GENERATION',
  [AdCreationTypes.TRAFFIC]: 'WEBSITE_TRAFFIC',
  [AdCreationTypes.SEARCH]: 'SEARCH',
  [AdCreationTypes.POST_ENGAGEMENT]: 'ENGAGEMENT'
};
export const AD_CREATION_REQUEST_TYPES = {
  LEAD_AD_CREATION: 'LEAD_AD_CREATION',
  TRAFFIC_AD_CREATION: 'TRAFFIC_AD_CREATION',
  SEARCH_AD_CREATION: 'SEARCH_AD_CREATION',
  SOCIAL_BOOST: 'SOCIAL_BOOST',
  SOCIAL_BOOST_WITH_CREATIVE: 'SOCIAL_BOOST_WITH_CREATIVE'
};
export const DEFAULT_CTA_BY_NETWORK = {
  [Networks.FACEBOOK]: FBLeadAdCallsToAction.VALUE_LEARN_MORE,
  [Networks.LINKEDIN]: LinkedInLeadAdCallsToAction.LEARN_MORE
};
export const TargetingTypes = {
  GENERAL: 'GENERAL',
  SEQUENCE: 'SEQUENCE',
  SEARCH: 'SEARCH'
};
export const AdStageStatusTypes = KeyMirror({
  ACTIVE: null,
  PAUSED: null,
  SCHEDULED: null
});
const TARGETING_RECORD_BY_TYPE = {
  [TargetingTypes.GENERAL]: AdCreationGeneralTargetingRecord,
  [TargetingTypes.SEQUENCE]: AdCreationSequenceTargetingRecord,
  [TargetingTypes.SEARCH]: AdCreationSearchTargetingRecord
};
export const TargetingAudienceOptions = {
  NEW_AUDIENCE: 'new',
  SAVED_AUDIENCE: 'saved'
};
export const AdCreationSteps = {
  AD: 'ad',
  TARGETING: 'targeting',
  SCHEDULE_AND_BUDGET: 'schedule-and-budget',
  AUTOMATION: 'automation',
  KEYWORDS: 'keywords'
};
export const AdSequenceSteps = {
  ATTRACT: 'attract',
  CONVERT: 'convert',
  CLOSE: 'close'
};
const SequenceAudienceTypes = {
  ALL_CUSTOMERS: 'ALL_CUSTOMERS',
  ENGAGED_LAST_30_DAYS: 'ENGAGED_LAST_30_DAYS',
  PEOPLE_WHO_VISIT_STEP_1: 'PEOPLE_WHO_VISIT_STEP_1',
  PEOPLE_WHO_WATCH_STEP_1: 'PEOPLE_WHO_WATCH_STEP_1',
  PEOPLE_WHO_SUBMIT_STEP_2: 'PEOPLE_WHO_SUBMIT_STEP_2',
  PEOPLE_WHO_SUBMIT_STEP_3: 'PEOPLE_WHO_SUBMIT_STEP_3',
  PEOPLE_WHO_CLICK_STEP_2: 'PEOPLE_WHO_CLICK_STEP_2',
  LOOKALIKE_AUDIENCE: 'LOOKALIKE_AUDIENCE'
};
const DEFAULT_EXCLUSIONS_BY_STEP = {
  [AdSequenceSteps.ATTRACT]: [SequenceAudienceTypes.ALL_CUSTOMERS, SequenceAudienceTypes.PEOPLE_WHO_SUBMIT_STEP_2, SequenceAudienceTypes.PEOPLE_WHO_CLICK_STEP_2, SequenceAudienceTypes.PEOPLE_WHO_SUBMIT_STEP_3],
  [AdSequenceSteps.CONVERT]: [SequenceAudienceTypes.ALL_CUSTOMERS, SequenceAudienceTypes.PEOPLE_WHO_SUBMIT_STEP_2, SequenceAudienceTypes.PEOPLE_WHO_CLICK_STEP_2, SequenceAudienceTypes.PEOPLE_WHO_SUBMIT_STEP_3],
  [AdSequenceSteps.CLOSE]: [SequenceAudienceTypes.ALL_CUSTOMERS, SequenceAudienceTypes.PEOPLE_WHO_SUBMIT_STEP_3]
};
const CAMPAIGN_OBJECTIVE_TO_CREATION_TYPES = {
  [FBCampaignObjective.LINK_CLICKS]: AdCreationTypes.TRAFFIC,
  [FBCampaignObjective.LEAD_GENERATION]: AdCreationTypes.LEAD_AD,
  [FBCampaignObjective.POST_ENGAGEMENT]: AdCreationTypes.POST_ENGAGEMENT
};
export const LeadAdAutomateRecord = Record({
  listName: '',
  listUrl: '',
  creatingList: false,
  listFailed: false,
  creatingWorkflow: false,
  workflowFailed: false,
  workflowId: null,
  workflowIds: List(),
  simpleWorkflowsOn: List(),
  workflowName: null,
  notificationEmails: List(),
  automationContactListName: null
});
export const PortfolioStrategyRecord = Record({
  name: null,
  id: null,
  createNewPortfolioStrategy: false,
  newPortfolioStrategyName: null,
  showPortfolioInput: false
});
export const BiddingStrategyInfoRecord = Record({
  hasTargetRoas: false,
  targetRoas: null,
  targetCpa: null,
  hasTargetCpa: false,
  targetCpc: null,
  hasTargetCpc: false,
  biddingStrategyType: null,
  portfolioStrategy: PortfolioStrategyRecord()
});
const LeadAdCreationReducerRecord = Record({
  mode: AD_MODE.CREATE,
  version: CURRENT_DRAFT_VERSION,
  targetingType: TargetingTypes.GENERAL,
  adType: null,
  campaignName: null,
  campaignId: null,
  adGroupId: null,
  adId: null,
  leadSyncingEntityId: null,
  adAccountId: null,
  creatives: List(),
  keywords: OrderedMap(),
  form: null,
  targeting: new AdCreationGeneralTargetingRecord(),
  schedule: ScheduleRecord(),
  budget: BudgetRecord(),
  automation: LeadAdAutomateRecord(),
  sequenceNotifications: null,
  immediatelyMarkContactsMarketable: false,
  leadSyncingEntityShouldCreateMarketingContacts: undefined,
  stageStatus: AdStageStatusTypes.SCHEDULED,
  biddingStrategyInfo: BiddingStrategyInfoRecord(),
  portfolioBiddingStrategyOptions: List(),
  offsiteDeliveryEnabled: null
});
export const loadFromPublished = (step, ads, FBTargeting) => {
  const {
    accountId: adAccountId,
    type: adType,
    name: campaignName,
    id: campaignId,
    startTime
  } = ads[`${step}StageCampaign`].entity;
  const {
    budget,
    rawTargeting
  } = ads[`${step}StageAdGroup`].entity;
  const {
    budgetId,
    amount: value,
    type
  } = budget;
  const {
    id: adId,
    pageId,
    adGroupId,
    body: text,
    formId,
    imageUrl
  } = ads[`${step}StageAd`].entity;
  const stageStatus = ads[`${step}StageStatus`];
  const {
    createdAdSequence
  } = ads.entity;
  const {
    convertCreatedAdId,
    closeCreatedAdId,
    attractCreatedAdId,
    convertAdStartType,
    attractAdEndType
  } = createdAdSequence;
  const postVideo = step === AdSequenceSteps.ATTRACT && (convertAdStartType === SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ScheduleTypes.VIDEO_VIEWS_MAX] || attractAdEndType === SCHEDULE_TYPE_TO_SEQUENCE_TYPE[ScheduleTypes.VIDEO_VIEWS_MAX]);
  return LeadAdCreationReducerRecord({
    mode: AD_MODE.EDIT,
    stageStatus,
    adType: CAMPAIGN_OBJECTIVE_TO_CREATION_TYPES[adType],
    adAccountId,
    adId,
    adGroupId,
    campaignId,
    campaignName,
    leadSyncingEntityId: pageId,
    form: formId && new LeadFormRecord({
      formId: formId && Number(formId)
    }),
    schedule: getScheduleFromPublished(createdAdSequence, step, startTime),
    targetingType: 'SEQUENCE',
    creatives: List([new CREATIVE_RECORD_BY_TYPE[CreativeTypes.PAID_SOCIAL]({
      useExistingPost: true,
      selectedPost: new FBSocialPostRecord({
        id: adId,
        media: ImmutableMap({
          url: imageUrl,
          type: FileTypes.IMG
        }),
        text,
        postType: postVideo ? 'VIDEO' : null
      })
    })]),
    targeting: AdCreationSequenceTargetingRecord.getTargetingFromPublishedAd(rawTargeting, ImmutableSet(DEFAULT_EXCLUSIONS_BY_STEP[step]), FBTargeting),
    budget: new BudgetRecord({
      budgetId,
      value,
      type
    }),
    sequenceNotifications: getNotificationFromPublished(ads.adSequenceNotificationSubscription, step, {
      convertCreatedAdId,
      closeCreatedAdId,
      attractCreatedAdId
    })
  });
};
export const loadFromDraft = (network, draft) => {
  if (draft.version > CURRENT_DRAFT_VERSION) {
    throw new Error('Unsupported draft version!');
  }

  const targetingType = draft.targetingType || TargetingTypes.GENERAL;
  const TargetingRecord = TARGETING_RECORD_BY_TYPE[targetingType];
  const campaignId = draft.version > 2 ? draft.campaignId : draft.creative && draft.creative.campaignId;
  const leadSyncingEntityId = draft.version > 2 ? draft.leadSyncingEntityId : draft.creative && draft.creative.leadSyncingEntityId;
  const budgetFromDraft = Object.assign({}, draft.budget, {
    biddingStrategy: draft.budget && draft.budget.biddingStrategy === GoogleBiddingStrategyTypes.ENHANCED_CPC ? GoogleBiddingStrategyTypes.TARGET_SPEND : draft.budget.biddingStrategy
  });
  return LeadAdCreationReducerRecord({
    version: CURRENT_DRAFT_VERSION,
    targetingType,
    campaignName: draft.campaignName,
    adAccountId: draft.adAccountId,
    adGroupId: draft.adGroupId || null,
    campaignId,
    leadSyncingEntityId,
    creatives: draft.version > 2 ? List(draft.creatives.map(creative => new CREATIVE_RECORD_BY_TYPE[creative.creativeType || CreativeTypes.PAID_SOCIAL](Object.assign({}, creative, {
      media: creative.media && fromJS(creative.media),
      websiteUrl: creative.websiteUrl && AdCreationWebsiteUrlRecord().merge(creative.websiteUrl || {})
    })))) : List([new CREATIVE_RECORD_BY_TYPE[draft.creative.creativeType || CreativeTypes.PAID_SOCIAL](Object.assign({}, draft.creative, {
      media: draft.creative.media && fromJS(draft.creative.media),
      websiteUrl: AdCreationWebsiteUrlRecord().merge(draft.websiteUrl || {})
    }))]),
    keywords: draft.keywords ? OrderedMap(Object.values(draft.keywords).map(keyword => [keyword.id, new KeywordCreationRecord(keyword)])) : OrderedMap(),
    form: draft.form && new LeadFormRecord(draft.form),
    targeting: TargetingRecord.getTargetingFromDraft(network, draft.targeting),
    schedule: getScheduleFromDraft(draft.schedule),
    budget: BudgetRecord(budgetFromDraft),
    automation: LeadAdAutomateRecord(Object.assign({}, draft.automation, {
      workflowIds: List(draft.automation.workflowIds),
      notificationEmails: List(draft.automation.notificationEmails),
      simpleWorkflowsOn: List(draft.automation.simpleWorkflowsOn)
    })),
    adType: draft.version === 1 ? AdCreationTypes.LEAD_AD : draft.adType,
    sequenceNotifications: draft.sequenceNotifications ? getNotificationFromDraft(draft.sequenceNotifications) : null,
    immediatelyMarkContactsMarketable: draft.immediatelyMarkContactsMarketable || false,
    // Undefined is a valid value for this, and also the default
    leadSyncingEntityShouldCreateMarketingContacts: draft.leadSyncingEntityShouldCreateMarketingContacts,
    biddingStrategyInfo: draft.biddingStrategyInfo && BiddingStrategyInfoRecord({
      biddingStrategyType: draft.biddingStrategyInfo.biddingStrategyType,
      hasTargetRoas: draft.biddingStrategyInfo.hasTargetRoas,
      targetRoas: draft.biddingStrategyInfo.targetRoas,
      hasTargetCpa: draft.biddingStrategyInfo.hasTargetCpa,
      targetCpa: draft.biddingStrategyInfo.targetCpa,
      hasTargetCpc: draft.biddingStrategyInfo.hasTargetCpc,
      targetCpc: draft.biddingStrategyInfo.targetCpc,
      portfolioStrategy: PortfolioStrategyRecord(draft.biddingStrategyInfo.portfolioStrategy)
    }),
    offsiteDeliveryEnabled: draft.offsiteDeliveryEnabled
  });
};
export default ((_ref = {}) => {
  let {
    creatives = [],
    targeting,
    targetingType: specifiedTargetingType,
    schedule: {
      startDateType = ScheduleTypes.FIXED_DATE,
      endDateType = ScheduleTypes.FIXED_DATE
    } = {},
    adType,
    budget
  } = _ref,
      scheduleProps = _objectWithoutPropertiesLoose(_ref.schedule, ["startDateType", "endDateType"]),
      fields = _objectWithoutPropertiesLoose(_ref, ["creatives", "targeting", "targetingType", "schedule", "adType", "budget"]);

  const defaultStartDate = I18n.moment().add(1, 'hours');
  const defaultTime = getMinutesSinceMidnight(defaultStartDate);
  const targetingType = adType === AdCreationTypes.SEARCH ? TargetingTypes.SEARCH : specifiedTargetingType || TargetingTypes.GENERAL;
  const TargetingRecord = TARGETING_RECORD_BY_TYPE[targetingType];
  return LeadAdCreationReducerRecord(Object.assign({
    adType,
    schedule: ScheduleRecord(Object.assign({
      startDateType,
      endDateType,
      startDate: fromMoment(defaultStartDate),
      startTime: defaultTime,
      endDate: null,
      endTime: defaultTime
    }, scheduleProps)),
    targetingType,
    creatives: List(creatives.map(creative => new CREATIVE_RECORD_BY_TYPE[creative.creativeType](creative))),
    targeting: new TargetingRecord(targeting),
    budget: new BudgetRecord(budget)
  }, fields));
});